import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const SplashContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
  background: ${props => props.$isDark ? 'linear-gradient(135deg, #0b0a12 0%, #151421 100%)' : 'linear-gradient(135deg, #f8f9fa 0%, #eef2f6 100%)'};
  color: ${props => props.$isDark ? '#f8fafc' : '#0f172a'};
  font-family: 'Inter', sans-serif;
  overflow: hidden;
  position: relative;
`;

const AmbientGlow = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, ${props => props.$isDark ? 'rgba(129, 140, 248, 0.12)' : 'rgba(79, 70, 229, 0.06)'} 0%, rgba(0,0,0,0) 70%);
  filter: blur(40px);
  pointer-events: none;
  animation: floatGlow 8s infinite ease-in-out;
  z-index: 1;

  @keyframes floatGlow {
    0%, 100% { transform: translate(-30px, -30px) scale(1); }
    50% { transform: translate(30px, 30px) scale(1.15); }
  }
`;

const GridBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${props => props.$isDark 
    ? 'linear-gradient(rgba(129, 140, 248, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(129, 140, 248, 0.03) 1px, transparent 1px)'
    : 'linear-gradient(rgba(79, 70, 229, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(79, 70, 229, 0.02) 1px, transparent 1px)'};
  background-size: 40px 40px;
  mask-image: radial-gradient(circle at 50% 50%, black, transparent 80%);
  -webkit-mask-image: radial-gradient(circle at 50% 50%, black, transparent 80%);
  z-index: 0;
  pointer-events: none;
`;

const LogoWrapper = styled.div`
  position: relative;
  z-index: 2;
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SVGLogo = styled.svg`
  width: 110px;
  height: 110px;
  color: ${props => props.$isDark ? '#818cf8' : '#4f46e5'};
  filter: drop-shadow(0 0 15px ${props => props.$isDark ? 'rgba(129, 140, 248, 0.4)' : 'rgba(79, 70, 229, 0.25)'});

  .hexagon {
    fill: none;
    stroke: currentColor;
    stroke-width: 4;
    stroke-linecap: round;
    stroke-dasharray: 320;
    stroke-dashoffset: 320;
    animation: drawHexagon 2.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }

  .logo-text {
    animation: fadeInText 1.2s ease-out 0.6s forwards;
    opacity: 0;
    fill: ${props => props.$isDark ? '#f8fafc' : '#0f172a'};
    text-shadow: 0 0 10px ${props => props.$isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'};
  }

  @keyframes drawHexagon {
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes fadeInText {
    to {
      opacity: 1;
    }
  }
`;

const Content = styled.div`
  text-align: center;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h1`
  font-family: 'Outfit', sans-serif;
  font-size: 2.4rem;
  font-weight: 800;
  letter-spacing: 6px;
  margin: 15px 0 5px 0;
  text-transform: uppercase;
  background: ${props => props.$isDark 
    ? 'linear-gradient(135deg, #f8fafc 0%, #94a3b8 100%)' 
    : 'linear-gradient(135deg, #0f172a 0%, #475569 100%)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: fadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  
  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Subtitle = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 5px;
  color: ${props => props.$isDark ? '#38bdf8' : '#0284c7'};
  text-transform: uppercase;
  margin-bottom: 40px;
  opacity: 0;
  animation: fadeUpSub 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards;

  @keyframes fadeUpSub {
    from {
      opacity: 0;
      transform: translateY(15px);
    }
    to {
      opacity: 0.8;
      transform: translateY(0);
    }
  }
`;

const ProgressContainer = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  height: 5px;
  background: ${props => props.$isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)'};
  border-radius: 10px;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  width: ${props => props.$progress}%;
  background: linear-gradient(90deg, 
    ${props => props.$isDark ? '#818cf8, #c084fc, #38bdf8' : '#4f46e5, #9333ea, #0284c7'}
  );
  box-shadow: 0 0 10px ${props => props.$isDark ? 'rgba(129, 140, 248, 0.4)' : 'rgba(79, 70, 229, 0.25)'};
  border-radius: 10px;
`;

const ProgressInfo = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-family: 'Space Grotesk', monospace;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.$isDark ? '#94a3b8' : '#475569'};
`;

const StatusMessage = styled.div`
  font-size: 0.72rem;
  font-family: 'Space Grotesk', monospace;
  letter-spacing: 1.5px;
  color: ${props => props.$isDark ? '#94a3b8' : '#475569'};
  text-transform: uppercase;
  margin-top: 4px;
  height: 18px;
  opacity: 0.85;
`;

const Splash = () => {
  const [isDark, setIsDark] = useState(true);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('INITIALIZING...');

  useEffect(() => {
    // Detect system dark mode preference
    const darkThemeMq = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDark(darkThemeMq.matches);

    const listener = (e) => setIsDark(e.matches);
    darkThemeMq.addEventListener('change', listener);

    // Increment progress bar to match the 2-second lazy-load delay
    const startTime = Date.now();
    const duration = 2000;

    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(Math.round((elapsed / duration) * 100), 100);
      setProgress(pct);

      // Dynamic terminal statuses based on progress percentage
      if (pct < 25) {
        setStatus('INITIALIZING SYSTEMS...');
      } else if (pct < 55) {
        setStatus('COMPILING WEB COMPONENTS...');
      } else if (pct < 75) {
        setStatus('LOADING DATA SERVICES...');
      } else if (pct < 95) {
        setStatus('OPTIMIZING ASSETS...');
      } else {
        setStatus('READY!');
      }

      if (pct >= 100) {
        clearInterval(timer);
      }
    }, 16);

    return () => {
      darkThemeMq.removeEventListener('change', listener);
      clearInterval(timer);
    };
  }, []);

  return (
    <SplashContainer $isDark={isDark}>
      <AmbientGlow $isDark={isDark} />
      <GridBackground $isDark={isDark} />
      
      <LogoWrapper>
        <SVGLogo viewBox="0 0 100 100" $isDark={isDark}>
          <path 
            className="hexagon" 
            d="M50 5 L90 28 L90 72 L50 95 L10 72 L10 28 Z" 
          />
          <text 
            className="logo-text"
            x="50%" 
            y="52%" 
            dominantBaseline="middle" 
            textAnchor="middle" 
            fontSize="34" 
            fontFamily="'Outfit', sans-serif" 
            fontWeight="800"
          >
            S
          </text>
        </SVGLogo>
      </LogoWrapper>

      <Content>
        <Title $isDark={isDark}>Srinithish</Title>
        <Subtitle $isDark={isDark}>Software Engineer</Subtitle>

        <ProgressContainer>
          <ProgressInfo $isDark={isDark}>
            <span>BOOTING</span>
            <span>{progress}%</span>
          </ProgressInfo>
          <ProgressBarWrapper $isDark={isDark}>
            <ProgressBarFill $progress={progress} $isDark={isDark} />
          </ProgressBarWrapper>
          <StatusMessage $isDark={isDark}>{status}</StatusMessage>
        </ProgressContainer>
      </Content>
    </SplashContainer>
  );
};

export default Splash;
